import React, { useState } from 'react';
import {
 Dialog,
 DialogContent,
 DialogHeader,
 DialogTitle
} from"../ui/Dialog";
import Input from"../ui/Input";
import Button from"../ui/Button";
import ScrollArea from"../ui/ScrollArea";
import {
 Sparkles,
 Code2,
 Brain,
 ClipboardList,
 FileSearch,
 Wrench
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../Context/SubscriptionContext';
import PremiumUpgradeModal from './PremiumUpgradeModal';

const tsgCommands = [
  {
    label: '🧠 Explain a Topic',
    intent: 'explain_deep',
    icon: <Brain className="w-5 h-5" />,
    description: 'Multilingual explanation with real-world examples'
  },
  {
    label: '🧪 Test My Understanding',
    intent: 'skill_test',
    icon: <ClipboardList className="w-5 h-5" />,
    description: 'Answer AI-generated questions to test concept mastery'
  },
  {
    label: '💻 Review My Code',
    intent: 'code_review',
    icon: <Code2 className="w-5 h-5" />,
    description: 'Find and fix bugs with AI suggestions'
  },
  {
    label: '👨‍💼 Generate Interview Q&A',
    intent: 'interview_qa',
    icon: <Sparkles className="w-5 h-5" />,
    description: 'Get questions and answers for job prep'
  },
  {
    label: '📄 Evaluate My Resume',
    intent: 'resume_review',
    icon: <FileSearch className="w-5 h-5" />,
    description: 'Get feedback on resume content, structure and ATS-readiness'
  },
  {
    label: '🛠 Project Builder',
    intent: 'project_builder',
    icon: <Wrench className="w-5 h-5" />,
    description: 'Build and manage projects interactively with AI assistance'
  }
];

export default function TSGCommandPalette({ open, onClose }) {
  const [search, setSearch] = useState('');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const navigate = useNavigate();
  const { canAccessFeature } = useSubscription();

  const filtered = tsgCommands.filter(cmd =>
    cmd.label.toLowerCase().includes(search.toLowerCase()) ||
    cmd.description.toLowerCase().includes(search.toLowerCase())
  );

  const handleCommand = (cmd) => {
    // Robust detection of tenant student context
    const host = typeof window !== 'undefined' ? (window.location?.hostname || '') : '';
    const isTenantHostname = (host.endsWith('.theskillgenie.com') && !host.startsWith('www.')) || 
                             (host !== 'localhost' && !host.includes('theskillgenie.com'));
    
    const storedUser = localStorage.getItem("user");
    let isCollegeUser = false;
    try {
        if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            isCollegeUser = parsedUser?.role === 'student' && (parsedUser?.tenant || parsedUser?.college_id || parsedUser?.institution_id);
        }
    } catch (e) {}

    const isTenantStudent = isTenantHostname || isCollegeUser;

    if (!canAccessFeature('TSG_AI')) {
      setShowUpgrade(true);
      return;
    }
    onClose(); // close the dialog first

    const lastRead = JSON.parse(localStorage.getItem('last_read_doc') || "{}");
    const tech = lastRead.tech || "AI";
    const query = `?tech=${encodeURIComponent(tech)}`;
    const routePrefix = isTenantStudent ? '/student/tsg' : '/tsg';

    switch (cmd.intent) {
      case 'explain_deep':
        navigate(routePrefix + '/explain' + query);
        break;
      case 'skill_test':
        navigate(routePrefix + '/skill-test' + query);
        break;
      case 'code_review':
        navigate(routePrefix + '/code-review' + query);
        break;
      case 'interview_qa':
        navigate(routePrefix + '/interview' + query);
        break;
      case 'resume_review':
        navigate(routePrefix + '/resume-review' + query);
        break;
      case 'project_builder':
        navigate(routePrefix + '/project-builder' + query);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Dialog isOpen={open} onClose={onClose}>
        <DialogHeader className="border-b border-gray-100 pb-4">
          <DialogTitle className="text-[#5B21B6] font-black uppercase tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            What can @tsg do?
          </DialogTitle>
        </DialogHeader>

        <DialogContent className="p-0">
          <div className="p-4 bg-purple-50/30">
            <Input
              placeholder="Search commands (e.g. resume, interview, code)"
              className="w-full border-purple-100 focus:border-[#5B21B6] focus:ring-[#5B21B6] rounded-xl"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <ScrollArea className="h-[400px] p-4">
            <div className="space-y-3">
              {filtered.map((cmd, idx) => (
                <Button
                  key={idx}
                  variant="ghost"
                  className="w-full justify-start text-left rounded-2xl py-4 px-5 transition-all border border-purple-50 bg-white hover:border-[#5B21B6] hover:bg-purple-50/50 group shadow-sm"
                  onClick={() => handleCommand(cmd)}
                >
                  <div className="flex items-center gap-4 w-full">
                    <div className="p-3 bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] text-white rounded-xl shadow-lg shadow-purple-200 transition-transform group-hover:scale-110">
                      {cmd.icon}
                    </div>
                    <div className="flex flex-col flex-1">
                      <div className="font-black text-sm text-[#111827] group-hover:text-[#5B21B6] transition-colors">{cmd.label}</div>
                      <div className="text-xs text-[#6B7280] font-medium mt-0.5">{cmd.description}</div>
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

 {showUpgrade && (
    <PremiumUpgradeModal 
        isOpen={showUpgrade} 
        onClose={() => setShowUpgrade(false)} 
    />
 )}
 </>
 );
}



